"use client";

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { authenticateAndCreateSession } from '@/utils/auth-helpers';
import { useUserSession } from '@/hooks/use-user-session';
import { useCSRF } from '@/contexts/csrf-context';
import { Button } from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import { csrfFetch } from '@/utils/api-client';

/**
 * Reusable Temple Signup CTA component for conversion shift
 * Used after Q1, Q2 to invite users to join Temple
 * Now handles saving readings before authentication and different messages for signed-in users
 */
function TempleSignupCTA({ 
  variant = 'q1', // 'q1', 'q2' (q3 removed - limit is now 2)
  onSkip,
  className = '',
  readingData = null, // Reading data to save: { readingType, question, answer, explanation, deeperInsight, confidence, locale, pendulum, dream, card, metadata }
  isPremium = false, // User's premium status
}) {
  const locale = useLocale();
  const router = useRouter();
  const userSessionId = useUserSession();
  const { refreshCSRF, csrfToken } = useCSRF();
  const [isProcessingSignIn, setIsProcessingSignIn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved', 'limit_reached', 'error'
  const [pendingCount, setPendingCount] = useState(null);
  const [savedCount, setSavedCount] = useState(null);
  const [saveLimitReached, setSaveLimitReached] = useState(false);
  const [isLoadingSaveLimit, setIsLoadingSaveLimit] = useState(false);
  const t = useTranslations('oracle.templeCta');
  const tButtons = useTranslations('common.header');
  const tSave = useTranslations('oracle.saveLimit');

  // Fetch saved count for signed-in users
  useEffect(() => {
    if (userSessionId && !isPremium) {
      setIsLoadingSaveLimit(true);
      fetch('/api/user/save-limit', { credentials: 'include' })
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setSavedCount(data.savedCount || 0);
          setSaveLimitReached(data.limitReached || false);
        })
        .catch(error => {
          // Silently handle network errors - they're expected in some scenarios
          if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            // Network error - likely offline or extension blocking
            return;
          }
          console.error('Failed to fetch save limit:', error);
        })
        .finally(() => {
          setIsLoadingSaveLimit(false);
        });
    }
  }, [userSessionId, isPremium]);

  // Determine user state category
  const getUserState = () => {
    if (!userSessionId) return 'anonymous';
    if (saveLimitReached) return 'limitReached';
    if (savedCount === null) return 'loading';
    if (savedCount >= 7) return 'nearLimit';
    if (savedCount >= 1) return 'active';
    return 'new';
  };

  const userState = getUserState();

  // Anonymous user variants (keep existing q1/q2)
  const anonymousVariants = {
    q1: {
      icon: '✨',
      title: t('q1.title', { defaultValue: '✨ Save This Reading' }),
      description: t('q1.description', { 
        defaultValue: 'Keep this clarity in your spiritual archive. Sign up free to save your readings, track patterns, and build your daily practice.' 
      }),
      primaryButton: t('q1.primaryButton', { defaultValue: 'Save to Temple' }),
      secondaryButton: t('q1.secondaryButton', { defaultValue: 'Skip for now' }),
    },
    q2: {
      icon: '📖',
      title: t('q2.title', { defaultValue: '📖 Join Temple for Unlimited' }),
      description: t('q2.description', { 
        defaultValue: "You've asked 2 powerful questions today. Your next clarity awaits tomorrow. But if you don't want to wait, the Temple gives you unlimited questions today + tomorrow + every day. Plus you'll build a 7-day streak, unlock achievements, and track your journey." 
      }),
      primaryButton: t('q2.primaryButton', { defaultValue: 'Join Temple Free' }),
      secondaryButton: t('q2.secondaryButton', { defaultValue: 'Return Tomorrow' }),
    },
  };

  // Signed-in user variants
  const signedInVariants = {
    new: {
      q1: {
        icon: '✨',
        title: t('signedInNew.q1.title', { defaultValue: '✨ Save This Reading' }),
        description: t('signedInNew.q1.description', { 
          defaultValue: 'Save this clarity to your Temple. Start building your spiritual archive.' 
        }),
        primaryButton: t('signedInNew.q1.primaryButton', { defaultValue: 'Save to Temple' }),
        secondaryButton: t('signedInNew.q1.secondaryButton', { defaultValue: 'Skip for now' }),
      },
      q2: {
        icon: '📖',
        title: t('signedInNew.q2.title', { defaultValue: '📖 Upgrade for Unlimited Questions' }),
        description: t('signedInNew.q2.description', { 
          defaultValue: "You've asked 2 questions today. Upgrade to unlock unlimited questions every day." 
        }),
        primaryButton: t('signedInNew.q2.primaryButton', { defaultValue: 'Upgrade to Unlimited' }),
        secondaryButton: t('signedInNew.q2.secondaryButton', { defaultValue: 'Return Tomorrow' }),
        upgradeButton: t('signedInNew.q2.upgradeButton', { defaultValue: 'Upgrade to Unlimited' }),
      },
    },
    active: {
      q1: {
        icon: '✨',
        title: t('signedInActive.q1.title', { defaultValue: '✨ Save This Reading' }),
        description: t('signedInActive.q1.description', { 
          count: savedCount || 0,
          defaultValue: 'Add this to your {count} saved readings. See patterns emerge across your journey.' 
        }),
        primaryButton: t('signedInActive.q1.primaryButton', { defaultValue: 'Save to Temple' }),
        secondaryButton: t('signedInActive.q1.secondaryButton', { defaultValue: 'Skip for now' }),
      },
      q2: {
        icon: '📖',
        title: t('signedInActive.q2.title', { defaultValue: '📖 Upgrade for Unlimited Questions' }),
        description: t('signedInActive.q2.description', { 
          defaultValue: 'Daily limit reached. Upgrade to unlock unlimited questions every day.' 
        }),
        primaryButton: t('signedInActive.q2.primaryButton', { defaultValue: 'Save to Temple' }),
        secondaryButton: t('signedInActive.q2.secondaryButton', { defaultValue: 'Return Tomorrow' }),
        upgradeButton: t('signedInActive.q2.upgradeButton', { defaultValue: 'Upgrade to Unlimited' }),
      },
    },
    nearLimit: {
      q1: {
        icon: '✨',
        title: t('signedInNearLimit.q1.title', { 
          remaining: savedCount ? (10 - savedCount) : 0,
          defaultValue: '✨ Save This Reading ({remaining} left)' 
        }),
        description: t('signedInNearLimit.q1.description', { 
          count: savedCount || 0,
          defaultValue: 'You\'ve saved {count} of 10 readings. Upgrade for unlimited access to your full journey.' 
        }),
        primaryButton: t('signedInNearLimit.q1.primaryButton', { defaultValue: 'Save to Temple' }),
        secondaryButton: t('signedInNearLimit.q1.secondaryButton', { defaultValue: 'Skip for now' }),
      },
      q2: {
        icon: '📖',
        title: t('signedInNearLimit.q2.title', { 
          remaining: savedCount ? (10 - savedCount) : 0,
          defaultValue: '📖 Upgrade for Unlimited' 
        }),
        description: t('signedInNearLimit.q2.description', { 
          count: savedCount || 0,
          defaultValue: 'Daily limit reached. Upgrade for unlimited questions and unlimited Reading History.' 
        }),
        primaryButton: t('signedInNearLimit.q2.primaryButton', { defaultValue: 'Save to Temple' }),
        secondaryButton: t('signedInNearLimit.q2.secondaryButton', { defaultValue: 'Return Tomorrow' }),
        upgradeButton: t('signedInNearLimit.q2.upgradeButton', { defaultValue: 'Upgrade to Unlimited' }),
      },
    },
    limitReached: {
      title: t('signedInLimitReached.title', { defaultValue: '📚 Upgrade to Save More' }),
      description: t('signedInLimitReached.description', { 
        defaultValue: 'You\'ve saved 10 readings. Upgrade to unlock unlimited Reading History and continue your journey.' 
      }),
      primaryButton: t('signedInLimitReached.primaryButton', { defaultValue: 'Upgrade to Unlimited' }),
    },
  };

  // Get the appropriate config based on user state and variant
  const getConfig = () => {
    if (userState === 'anonymous') {
      return anonymousVariants[variant] || anonymousVariants.q1;
    }
    
    if (userState === 'limitReached') {
      return signedInVariants.limitReached;
    }
    
    if (userState === 'loading') {
      // Return a default while loading
      return anonymousVariants[variant] || anonymousVariants.q1;
    }
    
    const stateVariants = signedInVariants[userState];
    if (stateVariants && stateVariants[variant]) {
      return stateVariants[variant];
    }
    
    // Fallback
    return anonymousVariants[variant] || anonymousVariants.q1;
  };

  const config = getConfig();
  const isQ2WithTwoButtons = variant === 'q2' && userSessionId && userState !== 'limitReached' && userState !== 'loading';

  // Save reading before authentication
  const saveReading = async () => {
    if (!readingData || !csrfToken) {
      return { success: false, error: 'Missing reading data or CSRF token' };
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const response = await csrfFetch('/api/readings/save', {
        method: 'POST',
        body: readingData,
      }, csrfToken);

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'save_limit_reached') {
          setSaveStatus('limit_reached');
          setSaveLimitReached(true);
          return { success: false, error: 'limit_reached', data };
        }
        throw new Error(data.message || 'Failed to save reading');
      }

      setSaveStatus('saved');
      if (data.pendingCount) {
        setPendingCount(data.pendingCount);
      }
      // Update saved count if user is signed in
      if (userSessionId && data.savedCount !== undefined) {
        setSavedCount(data.savedCount);
      }
      return { success: true, data };
    } catch (error) {
      console.error('Failed to save reading:', error);
      setSaveStatus('error');
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  };

  // Link anonymous readings after authentication
  const linkAnonymousReadings = async () => {
    if (!csrfToken) return;

    try {
      const response = await csrfFetch('/api/readings/link-anonymous', {
        method: 'POST',
      }, csrfToken);

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to link anonymous readings:', error);
      // Don't fail the flow if linking fails
      return { success: false };
    }
  };

  const handleSaveClick = async () => {
    // If user is authenticated, check save limit first
    if (userSessionId && !isPremium) {
      try {
        const response = await fetch('/api/user/save-limit', {
          credentials: 'include',
        });
        
        if (!response.ok) {
          // If we can't check the limit, proceed anyway (fail open)
          console.warn('Failed to check save limit, proceeding:', response.status);
        } else {
          const limitData = await response.json();

          if (limitData.limitReached) {
            setSaveStatus('limit_reached');
            setSaveLimitReached(true);
            return;
          }
        }
      } catch (error) {
        // Silently handle network errors - proceed with save attempt
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          // Network error - proceed anyway
          return;
        }
        console.error('Failed to check save limit:', error);
      }
    }

    // Save reading (works for both authenticated and anonymous)
    if (readingData) {
      const saveResult = await saveReading();
      if (!saveResult.success && saveResult.error === 'limit_reached') {
        return; // Stop here, limit reached message will be shown
      }
    }

    // If user is not logged in, authenticate first
    if (!userSessionId) {
      setIsProcessingSignIn(true);
      try {
        const result = await authenticateAndCreateSession(refreshCSRF);
        
        if (result.success && result.user) {
          // Link anonymous readings after authentication
          await linkAnonymousReadings();
          router.push(`/${locale}/temple`);
          setIsProcessingSignIn(false);
          return;
        } else if (!result.user && !result.error) {
          // Redirect flow initiated (mobile)
          setIsProcessingSignIn(false);
          return;
        } else if (result.error) {
          if (result.error.code === 'popup-closed') {
            console.log('Sign-in popup closed by user.');
          } else if (result.error.code === 'popup-cancelled') {
            console.log('Sign-in popup request cancelled.');
          } else if (result.error.code === 'redirect-cancelled') {
            console.log('Sign-in redirect operation was cancelled or failed.');
          } else {
            console.error('Error during authentication:', result.error);
            alert(result.error.message || 'An error occurred during the sign-in process. Please try again.');
          }
          setIsProcessingSignIn(false);
          return;
        }
      } catch (error) {
        console.error('Unexpected error during sign-in:', error);
        alert('An error occurred during the sign-in process. Please try again.');
        setIsProcessingSignIn(false);
        return;
      }
    } else {
      // User is already logged in, redirect to temple
      router.push(`/${locale}/temple`);
    }
  };

  const handleUpgradeClick = () => {
    router.push(`/${locale}/pricing`);
  };

  // Show limit reached message
  if (saveStatus === 'limit_reached' || userState === 'limitReached') {
    return (
      <div className={`mt-6 p-4 md:p-5 rounded-xl bg-gradient-to-br from-amber-900/50 to-purple-900/50 border border-amber-700/50 shadow-lg ${className}`}>
        <div className="text-center space-y-3">
          <h4 className="text-base md:text-lg font-semibold text-white" style={{ fontFamily: 'var(--font-marcellus)' }}>
            {config.title || tSave('limitReached.title', { defaultValue: '📚 Save Limit Reached' })}
          </h4>
          <p className="text-sm text-gray-300 leading-relaxed max-w-2xl mx-auto">
            {config.description || tSave('limitReached.message', { defaultValue: "You've reached your saving readings. Upgrade to unlock unlimited Reading History." })}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button
              variant="primary"
              onClick={handleUpgradeClick}
              className="w-full sm:w-auto px-6 py-2.5"
            >
              {config.primaryButton || tSave('limitReached.cta', { defaultValue: 'Upgrade to Unlimited' })}
            </Button>
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="text-sm text-gray-400 hover:text-gray-300 transition-colors underline-offset-2 hover:underline"
              >
                {config.secondaryButton || 'Skip'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-6 p-4 md:p-5 rounded-xl bg-gradient-to-br from-[#1a182c]/90 to-[#100e23]/90 border border-[#6A3683]/50 shadow-lg ${className}`}>
      <div className="text-center space-y-3">
        <h4 className="text-base md:text-lg font-semibold text-white" style={{ fontFamily: 'var(--font-marcellus)' }}>
          {config.title}
        </h4>
        <p className="text-sm text-gray-300 leading-relaxed max-w-2xl mx-auto">
          {config.description}
        </p>
        {saveStatus === 'saved' && pendingCount && (
          <p className="text-sm text-green-400 font-medium">
            {tSave('pendingCount', { count: pendingCount, defaultValue: `${pendingCount} reading${pendingCount !== 1 ? 's' : ''} saved! Sign in to view them in your Temple.` })}
          </p>
        )}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 ${isQ2WithTwoButtons ? 'flex-wrap' : ''}`}>
          <Button
            variant="primary"
            onClick={handleSaveClick}
            disabled={isProcessingSignIn || isSaving || !csrfToken || isLoadingSaveLimit}
            className="w-full sm:w-auto px-6 py-2.5"
          >
            {isProcessingSignIn || isSaving ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                <span>
                  {isSaving 
                    ? tSave('saving', { defaultValue: 'Saving...' })
                    : tButtons('buttons.loggingIn')
                  }
                </span>
              </span>
            ) : (
              config.primaryButton
            )}
          </Button>
          {isQ2WithTwoButtons && config.upgradeButton && (
            <Button
              variant="primary"
              onClick={handleUpgradeClick}
              className="w-full sm:w-auto px-6 py-2.5"
            >
              {config.upgradeButton}
            </Button>
          )}
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-gray-400 hover:text-gray-300 transition-colors underline-offset-2 hover:underline"
            >
              {config.secondaryButton}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

TempleSignupCTA.propTypes = {
  variant: PropTypes.oneOf(['q1', 'q2']), // Removed 'q3'
  onSkip: PropTypes.func,
  className: PropTypes.string,
  readingData: PropTypes.shape({
    readingType: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
    reframed: PropTypes.string,
    explanation: PropTypes.string,
    deeperInsight: PropTypes.string,
    confidence: PropTypes.number,
    locale: PropTypes.string,
    pendulum: PropTypes.object,
    dream: PropTypes.object,
    card: PropTypes.object,
    metadata: PropTypes.object,
  }),
  isPremium: PropTypes.bool,
};

TempleSignupCTA.defaultProps = {
  variant: 'q1',
  onSkip: null,
  className: '',
  readingData: null,
  isPremium: false,
};

export default TempleSignupCTA;
